import React from 'react';
import { AppSelect } from '../../../components/AppSelect';
import {
  APP_BTN_OUTLINE_CLASS,
  APP_BTN_PRIMARY_CLASS,
  APP_INPUT_CLASS,
  APP_SELECT_TRIGGER_CLASS,
  APP_TEXTAREA_CLASS,
} from '../../../ui/controlStyles';
import {
  DEFAULT_SKONTO_DAYS,
  formatPaymentTermsSentence,
  type PaymentTermsContext,
} from '../../../utils/paymentTerms';
import type { OfferDocumentPaymentTerms } from '../utils/offerDocumentMetaPatch';
import { normalizeSkontoDays, normalizeSkontoPercent } from '../utils/offerDocumentMetaPatch';
import { OfferFloatingEditShell } from './OfferFloatingEditShell';

const PRESET_DAYS = [7, 14, 30] as const;

const ZAHLUNGSZIEL_OPTIONS = [
  { value: 'immediate', label: 'Sofort' },
  { value: '7', label: '7 Tage' },
  { value: '14', label: '14 Tage' },
  { value: '30', label: '30 Tage' },
  { value: 'custom', label: 'Benutzerdefiniert' },
];

type PaymentSelectValue = 'immediate' | '7' | '14' | '30' | 'custom';

function selectValueFromTerms(terms: OfferDocumentPaymentTerms): PaymentSelectValue {
  if (terms.mode === 'immediate') return 'immediate';
  const days = terms.days ?? 7;
  if ((PRESET_DAYS as readonly number[]).includes(days as (typeof PRESET_DAYS)[number])) {
    return String(days) as '7' | '14' | '30';
  }
  return 'custom';
}

function sentenceFor(terms: OfferDocumentPaymentTerms, context: PaymentTermsContext): string {
  return formatPaymentTermsSentence({
    mode: terms.mode,
    days: terms.days,
    context,
    skontoPercent: terms.skontoPercent,
    skontoDays: terms.skontoDays,
  });
}

function normalizeTerms(terms: OfferDocumentPaymentTerms): OfferDocumentPaymentTerms {
  if (terms.mode === 'immediate') {
    return { mode: 'immediate', skontoPercent: null, skontoDays: null };
  }
  const skontoPercent = normalizeSkontoPercent(terms.skontoPercent);
  return {
    mode: 'net_days',
    days: terms.days ?? 7,
    skontoPercent,
    skontoDays: skontoPercent != null ? terms.skontoDays ?? DEFAULT_SKONTO_DAYS : null,
  };
}

export function OfferPaymentTermsModal({
  open,
  onClose,
  anchorRef,
  terms,
  wording,
  context,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  terms: OfferDocumentPaymentTerms;
  wording: string;
  context: PaymentTermsContext;
  onSave: (next: { terms: OfferDocumentPaymentTerms; wording: string }) => void | Promise<void>;
}) {
  const [draftTerms, setDraftTerms] = React.useState<OfferDocumentPaymentTerms>(terms);
  const [draftWording, setDraftWording] = React.useState(wording);
  const [daysDraft, setDaysDraft] = React.useState(String(terms.days ?? 21));
  const [skontoPercentDraft, setSkontoPercentDraft] = React.useState(
    terms.skontoPercent != null ? String(terms.skontoPercent).replace('.', ',') : '2',
  );
  const [skontoDaysDraft, setSkontoDaysDraft] = React.useState(
    String(terms.skontoDays ?? DEFAULT_SKONTO_DAYS),
  );
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    const next = normalizeTerms(terms);
    setDraftTerms(next);
    setDraftWording(wording.trim() ? wording : sentenceFor(next, context));
    setDaysDraft(String(next.days ?? 21));
    setSkontoPercentDraft(next.skontoPercent != null ? String(next.skontoPercent).replace('.', ',') : '2');
    setSkontoDaysDraft(String(next.skontoDays ?? DEFAULT_SKONTO_DAYS));
    setSaving(false);
  }, [open, terms, wording, context]);

  const selectValue = selectValueFromTerms(draftTerms);
  const skontoOn = draftTerms.mode !== 'immediate' && draftTerms.skontoPercent != null;
  const generated = sentenceFor(draftTerms, context);
  const wordingIsCustom = draftWording.trim().replace(/\u00a0/g, ' ') !== generated.replace(/\u00a0/g, ' ');

  const applyTerms = (next: OfferDocumentPaymentTerms) => {
    const normalized = normalizeTerms(next);
    setDraftTerms(normalized);
    setDraftWording(sentenceFor(normalized, context));
  };

  const save = async () => {
    let next = draftTerms;
    if (next.mode === 'net_days' && selectValue === 'custom') {
      const n = Number.parseInt(daysDraft.trim(), 10);
      next = normalizeTerms({
        ...next,
        days: Number.isFinite(n) && n >= 1 ? Math.min(9999, n) : 21,
      });
    }
    if (next.mode === 'net_days' && skontoOn) {
      const pct = normalizeSkontoPercent(skontoPercentDraft.replace(',', '.'));
      next = normalizeTerms({
        ...next,
        skontoPercent: pct,
        skontoDays: normalizeSkontoDays(skontoDaysDraft) ?? DEFAULT_SKONTO_DAYS,
      });
    }
    const wordingOut = draftWording.trim() || sentenceFor(next, context);
    setSaving(true);
    try {
      await Promise.resolve(onSave({ terms: next, wording: wordingOut }));
      onClose();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <OfferFloatingEditShell
      open={open}
      onClose={saving ? () => undefined : onClose}
      anchorRef={anchorRef}
      panelWidth={380}
      ariaLabel="Zahlungsziel bearbeiten"
    >
      <div className="mb-2 text-sm font-medium text-app-text">Zahlungsziel</div>
      <div className="space-y-3">
        <AppSelect
          aria-label="Zahlungsziel"
          value={selectValue}
          disabled={saving}
          className="w-full"
          triggerClassName={APP_SELECT_TRIGGER_CLASS}
          options={ZAHLUNGSZIEL_OPTIONS}
          onChange={(value) => {
            if (value === 'immediate') {
              applyTerms({ mode: 'immediate', skontoPercent: null, skontoDays: null });
              return;
            }
            if (value === 'custom') {
              const days =
                draftTerms.days && ![7, 14, 30].includes(draftTerms.days) ? draftTerms.days : 21;
              setDaysDraft(String(days));
              applyTerms({
                mode: 'net_days',
                days,
                skontoPercent: draftTerms.skontoPercent,
                skontoDays: draftTerms.skontoDays,
              });
              return;
            }
            applyTerms({
              mode: 'net_days',
              days: Number(value),
              skontoPercent: draftTerms.skontoPercent,
              skontoDays: draftTerms.skontoDays,
            });
          }}
        />
        {selectValue === 'custom' ? (
          <input
            type="text"
            inputMode="numeric"
            disabled={saving}
            aria-label="Zahlungsziel in Tagen"
            value={daysDraft}
            onChange={(e) => setDaysDraft(e.target.value)}
            onBlur={() => {
              const n = Number.parseInt(daysDraft.trim(), 10);
              const days = Number.isFinite(n) && n >= 1 ? Math.min(9999, n) : 21;
              setDaysDraft(String(days));
              applyTerms({ ...draftTerms, mode: 'net_days', days });
            }}
            className={APP_INPUT_CLASS}
            placeholder="Tage"
          />
        ) : null}

        <label
          className={`flex items-center gap-2 text-sm font-medium text-app-text ${
            draftTerms.mode === 'immediate' ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
          }`}
        >
          <input
            type="checkbox"
            className="rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue/30"
            checked={skontoOn}
            disabled={saving || draftTerms.mode === 'immediate'}
            onChange={(e) => {
              if (e.target.checked) {
                const pct = normalizeSkontoPercent(skontoPercentDraft.replace(',', '.')) ?? 2;
                const days = normalizeSkontoDays(skontoDaysDraft) ?? DEFAULT_SKONTO_DAYS;
                setSkontoPercentDraft(String(pct).replace('.', ','));
                setSkontoDaysDraft(String(days));
                applyTerms({
                  mode: 'net_days',
                  days: draftTerms.days ?? 7,
                  skontoPercent: pct,
                  skontoDays: days,
                });
                return;
              }
              applyTerms({
                mode: 'net_days',
                days: draftTerms.days ?? 7,
                skontoPercent: null,
                skontoDays: null,
              });
            }}
          />
          Skonto gewähren
        </label>
        {draftTerms.mode !== 'immediate' && skontoOn ? (
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              inputMode="decimal"
              disabled={saving}
              aria-label="Skonto in Prozent"
              value={skontoPercentDraft}
              onChange={(e) => setSkontoPercentDraft(e.target.value)}
              onBlur={() => {
                const pct = normalizeSkontoPercent(skontoPercentDraft.replace(',', '.')) ?? 2;
                setSkontoPercentDraft(String(pct).replace('.', ','));
                applyTerms({
                  ...draftTerms,
                  skontoPercent: pct,
                  skontoDays: normalizeSkontoDays(skontoDaysDraft) ?? DEFAULT_SKONTO_DAYS,
                });
              }}
              className={APP_INPUT_CLASS}
              placeholder="%"
            />
            <input
              type="text"
              inputMode="numeric"
              disabled={saving}
              aria-label="Skontofrist in Tagen"
              value={skontoDaysDraft}
              onChange={(e) => setSkontoDaysDraft(e.target.value)}
              onBlur={() => {
                const days = normalizeSkontoDays(skontoDaysDraft) ?? DEFAULT_SKONTO_DAYS;
                setSkontoDaysDraft(String(days));
                applyTerms({
                  ...draftTerms,
                  skontoPercent: normalizeSkontoPercent(skontoPercentDraft.replace(',', '.')) ?? 2,
                  skontoDays: days,
                });
              }}
              className={APP_INPUT_CLASS}
              placeholder="Tage"
            />
          </div>
        ) : null}

        <div>
          <div className="mb-1.5 flex items-center justify-between gap-3">
            <label className="text-sm font-medium text-app-text" htmlFor="offer-zahlungsziel-wording">
              Formulierung
            </label>
            {wordingIsCustom ? (
              <button
                type="button"
                disabled={saving}
                onClick={() => setDraftWording(generated)}
                className="text-xs font-medium text-pipedrive-blue hover:underline disabled:opacity-40"
              >
                Standardtext
              </button>
            ) : null}
          </div>
          <textarea
            id="offer-zahlungsziel-wording"
            value={draftWording}
            disabled={saving}
            rows={4}
            onChange={(e) => setDraftWording(e.target.value)}
            className={`${APP_TEXTAREA_CLASS} min-h-[4rem] resize-y leading-[1.45]`}
          />
        </div>
      </div>
      <div className="mt-3 flex justify-end gap-2 border-t border-app-border pt-3">
        <button type="button" onClick={onClose} disabled={saving} className={APP_BTN_OUTLINE_CLASS}>
          Abbrechen
        </button>
        <button type="button" onClick={() => void save()} disabled={saving} className={APP_BTN_PRIMARY_CLASS}>
          {saving ? 'Speichern…' : 'Speichern'}
        </button>
      </div>
    </OfferFloatingEditShell>
  );
}
